/*
 * Floral cardstock add-on
 *
 * When a floral product is added to cart with the "French Cardstock" property
 * checked, also add the G. Lalo Individual Handwritten Note companion product
 * ($10) so the customer is actually charged for the cardstock.
 *
 * Horizon's <product-form-component> intercepts the form submit event and calls
 * fetch(Theme.routes.cart_add_url) directly — a plain form.addEventListener('submit')
 * doesn't fire reliably. So we monkey-patch window.fetch to detect any /cart/add
 * request with our property and queue the companion add after it succeeds.
 *
 * Safe site-wide: no-op when the property isn't present in the request body.
 */
(function () {
  'use strict';

  var CARDSTOCK_VARIANT_ID = 52495826911508;
  var PROPERTY_KEY = 'French Cardstock';
  var COMPANION_FLAG = '__cardstock_companion__';

  // Track our own companion fetches so we don't recurse.
  var inflightCompanion = false;

  function bodyHasCardstockProperty(body) {
    if (!body) return false;
    try {
      if (body instanceof FormData) {
        var v = body.get('properties[' + PROPERTY_KEY + ']');
        return !!v && String(v).trim() !== '';
      }
      if (typeof body === 'string') {
        // Could be JSON or URL-encoded
        if (body.charAt(0) === '{') {
          var parsed = JSON.parse(body);
          // Skip if this is our companion add
          if (parsed[COMPANION_FLAG]) return false;
          if (parsed.properties && parsed.properties[PROPERTY_KEY]) {
            var pv = parsed.properties[PROPERTY_KEY];
            return !!pv && String(pv).trim() !== '';
          }
          return false;
        }
        // URL-encoded form data
        var key = 'properties%5B' + encodeURIComponent(PROPERTY_KEY) + '%5D=';
        var idx = body.indexOf(key);
        if (idx === -1) return false;
        var valStart = idx + key.length;
        var valEnd = body.indexOf('&', valStart);
        var raw = body.substring(valStart, valEnd === -1 ? body.length : valEnd);
        return raw.length > 0;
      }
      if (body instanceof URLSearchParams) {
        var v2 = body.get('properties[' + PROPERTY_KEY + ']');
        return !!v2 && String(v2).trim() !== '';
      }
    } catch (e) {
      console.warn('[cardstock] body inspection failed:', e);
    }
    return false;
  }

  function isCartAddUrl(url) {
    if (!url) return false;
    var u = String(url);
    return u.indexOf('/cart/add') !== -1;
  }

  async function addCompanion() {
    if (inflightCompanion) return;
    inflightCompanion = true;
    try {
      // Mirror Horizon's pattern: request rendered section HTML so the
      // cart-items component can morph in the new line item.
      var sectionIds = [];
      document.querySelectorAll('cart-items-component').forEach(function (el) {
        if (el.dataset && el.dataset.sectionId) {
          sectionIds.push(el.dataset.sectionId);
        }
      });

      var fd = new FormData();
      fd.append('id', String(CARDSTOCK_VARIANT_ID));
      fd.append('quantity', '1');
      if (sectionIds.length) fd.append('sections', sectionIds.join(','));

      var res = await origFetch.call(window, '/cart/add', {
        method: 'POST',
        body: fd,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'text/html'
        }
      });
      if (!res.ok) {
        console.warn('[cardstock] companion add failed:', res.status);
        return;
      }
      var data;
      try { data = await res.json(); } catch (e) { data = null; }

      // Dispatch cart:update so the icon count + drawer refresh.
      // Horizon's CartUpdateEvent is `new Event('cart:update', {bubbles:true})`
      // with `.detail = {resource, sourceId, data}` manually attached.
      try {
        var cartRes = await origFetch.call(window, '/cart.js');
        var cart = await cartRes.json();
        var evt = new Event('cart:update', { bubbles: true });
        evt.detail = {
          resource: cart,
          sourceId: 'floral-cardstock-addon',
          data: {
            source: 'floral-cardstock-addon',
            sections: data && data.sections ? data.sections : undefined,
            itemCount: 1,
            variantId: String(CARDSTOCK_VARIANT_ID)
          }
        };
        document.dispatchEvent(evt);
      } catch (e) {
        // Non-fatal — line item is in the cart even if UI didn't refresh.
      }
    } catch (e) {
      console.warn('[cardstock] companion add error:', e);
    } finally {
      inflightCompanion = false;
    }
  }

  var origFetch = window.fetch.bind(window);
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url);
    var body = init && init.body;
    // Some callers pass a Request object as `input` with the body inside it.
    if (!body && input && typeof input !== 'string' && input.body) {
      body = input.body;
    }

    var shouldChain = isCartAddUrl(url) && bodyHasCardstockProperty(body) && !inflightCompanion;
    var promise = origFetch(input, init);

    if (shouldChain) {
      promise.then(function (response) {
        // Clone before reading so we don't consume the body for the caller.
        var clone;
        try { clone = response.clone(); } catch (e) { clone = null; }
        if (!response.ok) return;
        // Best-effort: if response has a `status` field with a number, it's a Shopify error.
        if (clone) {
          clone.json().then(function (data) {
            if (data && typeof data.status === 'number') return; // error response
            addCompanion();
          }).catch(function () {
            // Not JSON (could be text/html with section data) — still treat as success
            addCompanion();
          });
        } else {
          addCompanion();
        }
      }).catch(function () { /* let original caller handle the error */ });
    }

    return promise;
  };

  // On floral product pages (which have the cardstock checkbox),
  // replace "Shipping calculated at checkout" → "Delivery calculated at checkout".
  // Arrangements are delivered locally in Dallas, not shipped.
  function rewriteShippingLabel() {
    if (!document.querySelector('input[type="checkbox"][name="properties[' + PROPERTY_KEY + ']"]')) {
      return;
    }
    // Horizon renders the line in <div class="tax-note">; older themes use <small>.
    document.querySelectorAll('.tax-note, small').forEach(function (el) {
      var t = el.textContent.replace(/\s+/g, ' ').trim();
      if (t === 'Shipping calculated at checkout.' || t === 'Shipping calculated at checkout') {
        el.textContent = 'Delivery calculated at checkout.';
      } else if (t === 'Taxes included. Shipping calculated at checkout.') {
        el.textContent = 'Taxes included. Delivery calculated at checkout.';
      }
    });
  }

  // Handwritten note toggle: the "_handwritten_note_toggle" checkbox controls
  // visibility of the textarea block below it. The toggle's name attribute is
  // stripped so the toggle itself doesn't become a line item property — it's
  // pure UI state. The textarea (property_key "Handwritten Note") is disabled
  // when hidden so an empty value doesn't get submitted.
  function initHandwrittenNoteToggle() {
    var toggle = document.querySelector(
      'product-custom-property-component[id$="__handwritten_note_toggle"] input[type="checkbox"]'
    );
    var textBlock = document.querySelector(
      'product-custom-property-component[id$="__handwritten_note_text"]'
    );
    if (!toggle || !textBlock) return;

    var textInput = textBlock.querySelector('textarea, input[type="text"]');

    // Strip the toggle's submit name so it stays out of cart line item properties.
    toggle.removeAttribute('name');

    function sync() {
      if (toggle.checked) {
        textBlock.classList.add('is-visible');
        if (textInput) {
          textInput.disabled = false;
          // Focus the textarea so the customer can start typing immediately
          // (only on user-initiated change, not initial sync).
        }
      } else {
        textBlock.classList.remove('is-visible');
        if (textInput) {
          textInput.disabled = true;
          textInput.value = '';
        }
      }
    }
    toggle.addEventListener('change', function () {
      sync();
      if (toggle.checked && textInput) textInput.focus();
    });
    sync();
  }

  // Cardstock description reveal: show the G. Lalo blurb only when the
  // cardstock checkbox is checked. CSS hides .__description by default;
  // we toggle .cardstock-revealed on the block to show it.
  function initCardstockReveal() {
    var block = document.querySelector(
      'product-custom-property-component[id$="__french_cardstock"]'
    );
    if (!block) return;
    var checkbox = block.querySelector('input[type="checkbox"]');
    if (!checkbox) return;

    function sync() {
      if (checkbox.checked) {
        block.classList.add('cardstock-revealed');
      } else {
        block.classList.remove('cardstock-revealed');
      }
    }
    checkbox.addEventListener('change', sync);
    sync();
  }

  function init() {
    rewriteShippingLabel();
    initHandwrittenNoteToggle();
    initCardstockReveal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
